/**
 * Distinct audio cues for the pack station (§14.4). Uses the WebAudio API to
 * synthesize short tones so no audio assets are needed and latency is minimal
 * (§2.3 — scan feedback must feel instant). Maps a PackCue to a tone profile.
 */
import type { PackCue } from "../packstation/machine.js";

type ToneSpec = { freq: number; durationMs: number; type: OscillatorType }[];

// Distinct, learnable cues — rising = good, harsh low = error.
const CUES: Partial<Record<PackCue, ToneSpec>> = {
  ITEM_OK: [{ freq: 880, durationMs: 70, type: "sine" }],
  LINE_COMPLETE: [
    { freq: 880, durationMs: 70, type: "sine" },
    { freq: 1320, durationMs: 110, type: "sine" },
  ],
  WRONG_ITEM: [{ freq: 160, durationMs: 320, type: "sawtooth" }],
  OVER_SCAN: [{ freq: 440, durationMs: 140, type: "triangle" }],
  WEIGHT_OK: [{ freq: 1046, durationMs: 120, type: "sine" }],
  WEIGHT_FAIL: [{ freq: 200, durationMs: 360, type: "sawtooth" }],
  // §15 LABEL_FIRST — actual ≠ theoretical; non-blocking, so a soft warning.
  WEIGHT_DISCREPANCY: [{ freq: 392, durationMs: 220, type: "triangle" }],
  // §33.1 — label confirmed (rising) vs wrong label (harsh, like a wrong item).
  LABEL_MATCHED: [
    { freq: 784, durationMs: 70, type: "sine" },
    { freq: 1175, durationMs: 110, type: "sine" },
  ],
  LABEL_MISMATCH: [{ freq: 150, durationMs: 380, type: "sawtooth" }],
  LABEL_PRINTED: [
    { freq: 660, durationMs: 80, type: "sine" },
    { freq: 990, durationMs: 80, type: "sine" },
    { freq: 1320, durationMs: 140, type: "sine" },
  ],
  LABEL_FAILED: [
    { freq: 200, durationMs: 200, type: "sawtooth" },
    { freq: 160, durationMs: 320, type: "sawtooth" },
  ],
  SHORT: [{ freq: 330, durationMs: 260, type: "triangle" }],
  PARKED: [{ freq: 520, durationMs: 160, type: "sine" }],
};

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  // Lazily create on first user gesture; browsers block autoplay otherwise.
  if (!ctx) {
    // webkitAudioContext fallback for older Chromium kiosk builds.
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
  }
  return ctx;
}

export function playCue(cue: PackCue): void {
  const spec = CUES[cue];
  if (!spec) return;
  const audio = getCtx();
  if (!audio) return;
  let t = audio.currentTime;
  for (const note of spec) {
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.type = note.type;
    osc.frequency.value = note.freq;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.25, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + note.durationMs / 1000);
    osc.connect(gain).connect(audio.destination);
    osc.start(t);
    osc.stop(t + note.durationMs / 1000);
    t += note.durationMs / 1000;
  }
}
